"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";

import {
  findRouteChain,
  findSettingsRouteByPathname,
} from "@/config/navigation";

export type TopBarActionsResolver = () => ReactNode | null | undefined;

type TopBarActionsRegistryContextValue = {
  resolvers: Readonly<Record<string, TopBarActionsResolver>>;
  register: (routePath: string, resolver: TopBarActionsResolver | null) => void;
};

const TopBarActionsRegistryContext =
  createContext<TopBarActionsRegistryContextValue | null>(null);

function useTopBarActionsRegistryContext(): TopBarActionsRegistryContextValue {
  const ctx = useContext(TopBarActionsRegistryContext);
  if (!ctx) {
    throw new Error(
      "Top bar actions hooks must be used within TopBarActionsRegistryProvider",
    );
  }
  return ctx;
}

export function TopBarActionsRegistryProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [resolvers, setResolvers] = useState<
    Record<string, TopBarActionsResolver>
  >({});

  const register = useCallback(
    (routePath: string, resolver: TopBarActionsResolver | null) => {
      setResolvers((prev) => {
        if (resolver == null) {
          if (!(routePath in prev)) return prev;
          const next = { ...prev };
          delete next[routePath];
          return next;
        }
        if (prev[routePath] === resolver) return prev;
        return { ...prev, [routePath]: resolver };
      });
    },
    [],
  );

  const value = useMemo(
    () => ({ resolvers, register }),
    [resolvers, register],
  );

  return (
    <TopBarActionsRegistryContext.Provider value={value}>
      {children}
    </TopBarActionsRegistryContext.Provider>
  );
}

/** Resolvers registered by list/detail pages (read by the shell top bar). */
export function useTopBarActionResolvers(): Readonly<
  Record<string, TopBarActionsResolver>
> {
  return useTopBarActionsRegistryContext().resolvers;
}

/**
 * Register top-bar actions for a nav route pattern. Cleared on unmount.
 * Pass a stable callback that returns the current actions (e.g. Export).
 */
export function useRegisterTopBarActions(
  routePath: string,
  resolver: TopBarActionsResolver,
): void {
  const { register } = useTopBarActionsRegistryContext();

  useEffect(() => {
    if (!routePath) return undefined;
    register(routePath, resolver);
    return () => register(routePath, null);
  }, [routePath, resolver, register]);
}

/** Register actions for the leaf route of the current pathname. */
export function useRegisterPageTopBarActions(resolver: TopBarActionsResolver): void {
  const pathname = usePathname() ?? "/";
  const routePath = useMemo(() => {
    const chain = findRouteChain(pathname);
    const fromChain = chain[chain.length - 1]?.path;
    if (fromChain) return fromChain;
    return findSettingsRouteByPathname(pathname)?.path ?? "";
  }, [pathname]);

  useRegisterTopBarActions(routePath, resolver);
}
